import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  commentsService,
  CreateCommentData,
  UpdateCommentData,
} from '@/services/comments.service';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { notifyApiError } from '@/lib/api-message';

export const commentKeys = {
  all: ['comments'] as const,
  byLesson: (lessonId: string) =>
    [...commentKeys.all, 'lesson', lessonId] as const,
  replies: (commentId: string) =>
    [...commentKeys.all, 'replies', commentId] as const,
};

export function useComments(
  lessonId: string,
  page: number = 1,
  limit: number = 20,
) {
  return useQuery({
    queryKey: [...commentKeys.byLesson(lessonId), page, limit],
    queryFn: () => commentsService.findByLesson(lessonId, page, limit),
    staleTime: 30000, // 30 seconds
  });
}

export function useCommentReplies(commentId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: commentKeys.replies(commentId),
    queryFn: () => commentsService.findReplies(commentId),
    enabled,
    staleTime: 30000,
  });
}

export function useCreateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCommentData) => commentsService.create(data),
    onSuccess: (_, variables) => {
      // Invalidar cache da aula
      queryClient.invalidateQueries({
        queryKey: commentKeys.byLesson(variables.lessonId),
      });

      // Se for resposta, invalidar cache do comentário pai
      if (variables.parentId) {
        queryClient.invalidateQueries({
          queryKey: commentKeys.replies(variables.parentId),
        });
      }

      toast.success(i18n.t('comments:toast.created'));
    },
    onError: (error: any) => {
      notifyApiError(error, i18n.t('comments:toast.createError'));
    },
  });
}

export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentId,
      data,
    }: {
      commentId: string;
      data: UpdateCommentData;
    }) => commentsService.update(commentId, data),
    onSuccess: () => {
      // Invalidar todos os comentários para atualizar
      queryClient.invalidateQueries({ queryKey: commentKeys.all });
      toast.success(i18n.t('comments:toast.updated'));
    },
    onError: (error: any) => {
      notifyApiError(error, i18n.t('comments:toast.updateError'));
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => commentsService.delete(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.all });
      toast.success(i18n.t('comments:toast.deleted'));
    },
    onError: (error: any) => {
      notifyApiError(error, i18n.t('comments:toast.deleteError'));
    },
  });
}

export function useTogglePinComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => commentsService.togglePin(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.all });
      toast.success(i18n.t('comments:toast.pinToggled'));
    },
    onError: (error: any) => {
      notifyApiError(error, i18n.t('comments:toast.pinError'));
    },
  });
}
